import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiCalendar, FiBot } = FiIcons;

function HeroSection() {
  return (
    <section className="relative min-h-screen gradient-bg flex items-center overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left"
          >
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              Halifax's Next Vehicle.
              <span className="block bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Smarter, Faster, 24/7.
              </span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl">
              Meet Halifax's AI-powered dealership platform—where every Maritime interaction is personalized, 
              every question gets answered, and your perfect vehicle finds you through Nova Scotia winters.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiSearch} className="w-5 h-5" />
                <span>Browse Halifax Inventory</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiCalendar} className="w-5 h-5" />
                <span>Book Halifax Service</span>
              </motion.button>
            </div>
          </motion.div>

          {/* Right Content - AI Assistant Preview */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="glass-effect rounded-2xl p-8 ai-glow">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center animate-pulse-glow">
                  <SafeIcon icon={FiBot} className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">AutoBot Halifax AI</h3>
                  <p className="text-green-400 text-sm">● Online in Halifax</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-blue-600/20 rounded-lg p-4 border-l-4 border-blue-400">
                  <p className="text-white">
                    👋 Hi! I'm AutoBot for Halifax drivers. Need help finding an AWD vehicle for winter, 
                    or want to book service before the snow flies?
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors">
                    Winter Ready Cars
                  </button>
                  <button className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm hover:bg-gray-700 transition-colors">
                    Halifax Service
                  </button>
                  <button className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm hover:bg-gray-700 transition-colors">
                    CAD Trade Value
                  </button>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="absolute -top-4 -right-4 w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full opacity-80 blur-sm"
            ></motion.div>
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="absolute -bottom-4 -left-4 w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full opacity-60 blur-sm"
            ></motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default HeroSection;